import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, Send, Loader2, ArrowLeft, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessagesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [unread, setUnread] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const loadUnread = async () => {
    try {
      const res = await axios.get(`${API}/admin/unread-messages`, { headers: getAuthHeaders() });
      setUnread(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadMessages = async (orderId) => {
    try {
      const res = await axios.get(`${API}/admin/order-messages/${orderId}`, { headers: getAuthHeaders() });
      setMessages(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadUnread(); const iv = setInterval(loadUnread, 10000); return () => clearInterval(iv); }, []);
  useEffect(() => { if (selectedOrder) { loadMessages(selectedOrder); const iv = setInterval(() => loadMessages(selectedOrder), 5000); return () => clearInterval(iv); } }, [selectedOrder]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedOrder) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/order-messages/${selectedOrder}`, { message: newMsg }, { headers: getAuthHeaders() });
      setNewMsg('');
      loadMessages(selectedOrder);
    } catch (e) { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  if (selectedOrder) {
    return (
      <div className="space-y-4" data-testid="admin-chat-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedOrder(null); loadUnread(); }} data-testid="back-to-inbox">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <h2 className="font-semibold text-slate-900">Chat - Orden {selectedOrder.slice(0, 8).toUpperCase()}</h2>
        </div>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="h-[400px] overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && <p className="text-center text-slate-400 py-8">Sin mensajes</p>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${m.sender_role === 'admin' ? 'bg-blue-600 text-white' : 'bg-white border text-slate-800'}`}>
                  {m.sender_role === 'cliente' && <p className="text-xs font-semibold text-emerald-600 mb-0.5">{m.sender_name}</p>}
                  <p className="text-sm">{m.message}</p>
                  <p className={`text-[10px] mt-1 ${m.sender_role === 'admin' ? 'text-blue-200' : 'text-slate-400'}`}>
                    {new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2 p-3 border-t bg-white">
            <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Responder al cliente..." className="flex-1" onKeyDown={(e) => e.key === 'Enter' && sendMessage()} data-testid="admin-chat-input" />
            <Button onClick={sendMessage} disabled={sending} className="bg-blue-700 hover:bg-blue-800" data-testid="admin-chat-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mensajes de Clientes</h1>
        <p className="text-slate-500">{unread.length} conversaciones con mensajes sin leer</p>
      </div>

      {unread.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <MessageCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay mensajes sin leer</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {unread.map((u) => (
            <Card key={u.order_id} className="p-4 border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedOrder(u.order_id)} data-testid={`inbox-${u.order_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{u.client_name}</p>
                    <p className="text-sm text-slate-500">{u.service_type} — Orden {u.order_id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{u.last_message}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold">{u.count}</span>
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(u.last_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessagesPage;
